/**
 * Little Joe — Cart drawer + AJAX add-to-cart + fly-to-cart animation
 *
 * Comportamiento:
 *  1. Click en cualquier <form action="/cart/add"> → intercept, fetch /cart/add.js
 *  2. Mientras carga: imagen del producto se clona y vuela hacia el cart icon
 *  3. Al llegar: sparkle burst en el icon + bounce + count update
 *  4. Toast notification "✓ Agregado al carrito" 3.5s
 *  5. Drawer NO se abre automáticamente (toast + pulse es el feedback)
 *  6. Click en cart icon → abre drawer (slide desde derecha)
 *  7. Quantity changes en drawer → /cart/change.js + re-render via Section Rendering API
 *
 * Eventos custom emitidos en `document`:
 *   - 'lj:cart:add'    detail: { item, cart }
 *   - 'lj:cart:update' detail: { cart }
 *   - 'lj:cart:open'
 *   - 'lj:cart:close'
 *
 * Seguridad: NO usamos innerHTML con strings concatenados. Para Section
 * Rendering API parseamos el HTML retornado por Shopify (server-side escapeado
 * por Liquid) y movemos nodos vía replaceChildren. Para recommendations
 * construimos DOM con createElement + textContent/src.
 */
(function () {
  'use strict';

  const SECTION_ID = 'lj-cart-drawer';
  const TOAST_DURATION = 3500;
  const FLY_DURATION = 700;

  // ─────────────────────────────────────────────────────────
  // DOM helpers
  // ─────────────────────────────────────────────────────────
  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function moneyFmt(cents) {
    return 'S/. ' + (cents / 100).toFixed(2);
  }

  // ─────────────────────────────────────────────────────────
  // Cart Drawer open/close
  // ─────────────────────────────────────────────────────────
  function openDrawer() {
    const drawer = $('[data-lj-cart-drawer]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lj-cart-open');
    document.dispatchEvent(new CustomEvent('lj:cart:open'));
    const closeBtn = drawer.querySelector('[data-lj-cart-close]');
    setTimeout(() => closeBtn?.focus(), 350);
  }

  function closeDrawer() {
    const drawer = $('[data-lj-cart-drawer]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lj-cart-open');
    document.dispatchEvent(new CustomEvent('lj:cart:close'));
  }

  // ─────────────────────────────────────────────────────────
  // Apply rendered section HTML to the live drawer.
  // Recibe el HTML del section (que Shopify renderiza server-side) y lo
  // mueve a vivo via replaceChildren (NO innerHTML string — XSS-safe).
  // Centralizado: tanto add-to-cart como change-qty pasan el HTML aquí.
  // ─────────────────────────────────────────────────────────
  function applyDrawerHtml(html) {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = parsed.querySelector('[data-lj-cart-drawer]');
    const oldDrawer = $('[data-lj-cart-drawer]');
    if (!newDrawer || !oldDrawer) return;

    const wasOpen = oldDrawer.getAttribute('aria-hidden') === 'false';
    oldDrawer.replaceChildren(...newDrawer.childNodes);
    if (wasOpen) oldDrawer.setAttribute('aria-hidden', 'false');

    hydrateRecommendations();
  }

  // Standalone refresh fallback — solo se usa si por alguna razón no
  // pudimos obtener el HTML inline en la respuesta de /cart/change o /cart/add.
  async function refreshDrawer() {
    try {
      const res = await fetch(`/?sections=${SECTION_ID}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      applyDrawerHtml(data[SECTION_ID]);
    } catch (err) {
      console.warn('[lj-cart] refresh failed', err);
    }
  }

  // ─────────────────────────────────────────────────────────
  // Cart count + total in header
  // ─────────────────────────────────────────────────────────
  function updateHeaderCount(cart) {
    // Soporta ambos headers: el clásico (.lj-cart-pill, con __amount) y el
    // Typhouse v2 (.th-cart-pill, cuyo monto es un <span> simple). Sin esto
    // la pastilla del header quedaba congelada con los valores del page load
    // mientras el drawer mostraba el estado real (bug Pedro 2026-07-10).
    const pill = $('.lj-cart-pill') || $('.th-cart-pill');
    if (!pill) return;
    const isTh = pill.classList.contains('th-cart-pill');
    const countClass = isTh ? 'th-cart-pill__count' : 'lj-cart-pill__count';

    const amountEl = isTh
      ? pill.querySelector('span:not(.th-cart-pill__count)')
      : pill.querySelector('.lj-cart-pill__amount');
    if (amountEl) amountEl.textContent = moneyFmt(cart.total_price);

    let countEl = pill.querySelector('.' + countClass);
    if (cart.item_count > 0) {
      if (!countEl) {
        countEl = document.createElement('span');
        countEl.className = countClass;
        pill.appendChild(countEl);
      }
      countEl.textContent = String(cart.item_count);
    } else if (countEl) {
      countEl.remove();
    }

    pill.classList.remove('is-pulsing');
    void pill.offsetWidth;
    pill.classList.add('is-pulsing');
  }

  // ─────────────────────────────────────────────────────────
  // Fly-to-cart animation
  // ─────────────────────────────────────────────────────────
  function flyToCart(sourceImg, onComplete) {
    if (!sourceImg) { onComplete?.(); return; }
    const cartIcon = $('.lj-cart-pill');
    if (!cartIcon) { onComplete?.(); return; }

    const srcRect = sourceImg.getBoundingClientRect();
    const tgtRect = cartIcon.getBoundingClientRect();

    // Si la imagen fuente no es visible (height/width 0), no animar.
    if (srcRect.width === 0 || srcRect.height === 0) {
      // Aún hacer el sparkle para feedback al user en mobile
      sparkleBurst(cartIcon);
      onComplete?.();
      return;
    }

    const clone = sourceImg.cloneNode(true);
    clone.removeAttribute('id');
    clone.style.cssText = `
      position: fixed;
      top: ${srcRect.top}px;
      left: ${srcRect.left}px;
      width: ${srcRect.width}px;
      height: ${srcRect.height}px;
      pointer-events: none;
      z-index: 9999;
      border-radius: 12px;
      box-shadow: 0 12px 32px rgba(0,0,0,0.25);
      transition: transform ${FLY_DURATION}ms cubic-bezier(0.5, -0.2, 0.7, 1.2),
                  opacity ${FLY_DURATION}ms ease-in,
                  border-radius ${FLY_DURATION}ms ease-out;
      will-change: transform, opacity;
    `;
    // CRITICAL en mobile: appendear al <html> en vez de <body> porque
    // body tiene `overflow-x: clip` que puede clipear elementos fixed
    // en algunos browsers (iOS Safari especialmente). documentElement no
    // tiene overflow constraints.
    document.documentElement.appendChild(clone);
    void clone.offsetWidth;

    const dx = (tgtRect.left + tgtRect.width / 2) - (srcRect.left + srcRect.width / 2);
    const dy = (tgtRect.top + tgtRect.height / 2) - (srcRect.top + srcRect.height / 2);

    clone.style.transform = `translate(${dx}px, ${dy}px) scale(0.15) rotate(360deg)`;
    clone.style.opacity = '0.3';
    clone.style.borderRadius = '50%';

    setTimeout(() => {
      clone.remove();
      sparkleBurst(cartIcon);
      onComplete?.();
    }, FLY_DURATION);
  }

  // ─────────────────────────────────────────────────────────
  // Sparkle burst + bounce
  // ─────────────────────────────────────────────────────────
  function sparkleBurst(target) {
    const rect = target.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const colors = ['#f26522', '#f5b700', '#3fbfcb', '#c2185b', '#fff'];
    const particles = 12;

    for (let i = 0; i < particles; i++) {
      const p = document.createElement('span');
      p.className = 'lj-sparkle';
      const angle = (Math.PI * 2 * i) / particles + Math.random() * 0.5;
      const distance = 35 + Math.random() * 30;
      p.style.cssText = `
        position: fixed;
        top: ${cy}px;
        left: ${cx}px;
        width: 8px;
        height: 8px;
        background: ${colors[i % colors.length]};
        border-radius: 50%;
        pointer-events: none;
        z-index: 9998;
        --tx: ${Math.cos(angle) * distance}px;
        --ty: ${Math.sin(angle) * distance}px;
      `;
      document.body.appendChild(p);
      requestAnimationFrame(() => p.classList.add('is-burst'));
      setTimeout(() => p.remove(), 700);
    }

    target.classList.remove('is-bouncing');
    void target.offsetWidth;
    target.classList.add('is-bouncing');
    setTimeout(() => target.classList.remove('is-bouncing'), 600);
  }

  // ─────────────────────────────────────────────────────────
  // Toast (uses textContent + .src — no innerHTML strings)
  // ─────────────────────────────────────────────────────────
  let toastTimer;
  function showToast({ title, imgSrc }) {
    const toast = $('[data-lj-toast]');
    if (!toast) return;

    const titleEl = toast.querySelector('[data-lj-toast-title]');
    const imgEl   = toast.querySelector('[data-lj-toast-img]');

    if (titleEl) titleEl.textContent = title || 'Agregado al carrito';
    if (imgEl) {
      if (imgSrc) {
        imgEl.src = imgSrc;
        imgEl.style.display = '';
      } else {
        imgEl.style.display = 'none';
      }
    }

    toast.setAttribute('aria-hidden', 'false');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, TOAST_DURATION);
  }

  function hideToast() {
    const toast = $('[data-lj-toast]');
    if (!toast) return;
    toast.setAttribute('aria-hidden', 'true');
    // Limpiar inline styles del swipe — para que el próximo show funcione bien
    toast.style.transform = '';
    toast.style.opacity = '';
    toast.style.transition = '';
  }

  // ─────────────────────────────────────────────────────────
  // Swipe-up to dismiss (gesto iOS-style — Pedro pidió 2026-05-03)
  // ─────────────────────────────────────────────────────────
  // Init guard previene doble-bind si cart.js se ejecuta 2 veces.
  function initToastSwipe() {
    if (window.__ljToastSwipeBound) return;
    const toast = $('[data-lj-toast]');
    if (!toast) return;
    window.__ljToastSwipeBound = true;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    let hasMovedSignificantly = false;
    const DISMISS_THRESHOLD = 50; // px hacia arriba para descartar

    toast.addEventListener('touchstart', function (e) {
      // Solo si está visible
      if (toast.getAttribute('aria-hidden') === 'true') return;
      startY = e.touches[0].clientY;
      currentY = startY;
      isDragging = true;
      hasMovedSignificantly = false;
      // Cortar transition para que el drag se sienta directo (no laggy)
      toast.style.transition = 'none';
    }, { passive: true });

    toast.addEventListener('touchmove', function (e) {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      const delta = currentY - startY;
      if (Math.abs(delta) > 6) hasMovedSignificantly = true;
      // Solo permitir drag hacia arriba (delta negativo)
      if (delta < 0) {
        toast.style.transform = `translateY(${delta}px)`;
        // Fade opacity progresivamente — más arriba = más transparente
        toast.style.opacity = String(Math.max(0, 1 + delta / 80));
      } else {
        // Si arrastra hacia abajo, no hacer nada (no rubber band)
        toast.style.transform = '';
      }
    }, { passive: true });

    toast.addEventListener('touchend', function () {
      if (!isDragging) return;
      isDragging = false;
      const delta = currentY - startY;
      // Restaurar transition smooth para que la animación final sea suave
      toast.style.transition = '';

      if (delta < -DISMISS_THRESHOLD) {
        // Descartar: animar fuera de pantalla y limpiar
        toast.style.transform = 'translateY(-160%)';
        toast.style.opacity = '0';
        clearTimeout(toastTimer);
        setTimeout(hideToast, 220);
      } else {
        // Snap back a posición original
        toast.style.transform = '';
        toast.style.opacity = '';
      }
    }, { passive: true });
    toast.addEventListener('touchcancel', function () {
      if (!isDragging) return;
      isDragging = false;
      toast.style.transition = '';
      toast.style.transform = '';
      toast.style.opacity = '';
    }, { passive: true });

    // Si el usuario hizo swipe (no tap puro), cancelar el click sobre
    // "Ver carrito" para que no navegue mientras descarta el toast.
    toast.addEventListener('click', function (e) {
      if (hasMovedSignificantly) {
        e.preventDefault();
        e.stopPropagation();
        hasMovedSignificantly = false;
      }
    }, true);
  }
  // Inicializar al cargar — el toast ya existe en el DOM (snippet incluido en theme.liquid)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initToastSwipe);
  } else {
    initToastSwipe();
  }

  // ─────────────────────────────────────────────────────────
  // AJAX add to cart
  // ─────────────────────────────────────────────────────────
  async function ajaxAddToCart(form, sourceImg) {
    // FormData del form + appendear `sections` para que el server retorne
    // el HTML del drawer y el JSON del item EN EL MISMO request (atómico).
    const formData = new FormData(form);
    formData.append('sections', SECTION_ID);
    formData.append('sections_url', window.location.pathname);

    const submitBtn = form.querySelector('[type="submit"]');
    const originalLabel = submitBtn?.textContent;

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      submitBtn.textContent = 'Agregando…';
    }

    // Disparar el fly-to-cart INMEDIATAMENTE — antes del fetch.
    // Si la imagen no es visible (off-screen), no animar pero sí seguir.
    flyToCart(sourceImg);

    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        cache: 'no-store',
      });

      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        throw new Error(err.description || 'No se pudo agregar al carrito');
      }

      const data = await addRes.json();
      // Cuando se usa `sections`, el response JSON tiene shape:
      //   { items: [...], sections: {...}, ...productItem }
      // Si NO hay sections en el response, fallback a fetch /cart.js
      const sectionHtml = data.sections?.[SECTION_ID];
      let cart;
      if (sectionHtml) {
        applyDrawerHtml(sectionHtml);
        // El response también incluye stats del cart en root level
        cart = { total_price: 0, item_count: 0, ...data };
        // Si no vienen totals, fetch de /cart.js
        if (!data.total_price) {
          const cr = await fetch('/cart.js', { cache: 'no-store' });
          cart = await cr.json();
        }
      } else {
        const cartRes = await fetch('/cart.js', { cache: 'no-store' });
        cart = await cartRes.json();
        await refreshDrawer();
      }

      updateHeaderCount(cart);
      showToast({
        title: '✓ ' + (data.product_title || data.items?.[0]?.product_title || '') + ' agregado',
        imgSrc: data.image || data.items?.[0]?.image,
      });

      if (submitBtn) {
        submitBtn.textContent = '✓ Agregado';
        submitBtn.classList.remove('is-loading');
        submitBtn.classList.add('is-success');
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-success');
          submitBtn.textContent = originalLabel;
        }, 1800);
      }

      document.dispatchEvent(new CustomEvent('lj:cart:add', { detail: { item: data, cart } }));
    } catch (err) {
      console.error('[lj-cart] add failed', err);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
        submitBtn.textContent = originalLabel;
      }
      showToast({
        title: '⚠️ ' + (err.message || 'Error al agregar'),
        imgSrc: null,
      });
    }
  }

  // ─────────────────────────────────────────────────────────
  // Quantity change in drawer
  // ─────────────────────────────────────────────────────────
  /**
   * Change quantity para UN line item.
   * Acepta string (item.key, recomendado por estabilidad) o number (line index).
   * Por qué key > line: con packs agrupados visualmente, el `forloop.index` del
   * loop filtrado NO coincide con el line real del cart (e.g. items individuales
   * después de items de pack). Key es único e inmutable.
   */
  async function changeQty(lineOrKey, quantity) {
    const body = { quantity, sections: SECTION_ID, sections_url: window.location.pathname };
    if (typeof lineOrKey === 'string') {
      body.id = lineOrKey;
    } else {
      body.line = lineOrKey;
    }
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('change failed: ' + res.status);
      const data = await res.json();
      const sectionHtml = data.sections?.[SECTION_ID];
      updateHeaderCount(data);
      if (sectionHtml) {
        applyDrawerHtml(sectionHtml);
      } else {
        await refreshDrawer();
      }
      document.dispatchEvent(new CustomEvent('lj:cart:update', { detail: { cart: data } }));
    } catch (err) {
      console.warn('[lj-cart] change failed', err);
    }
  }

  /**
   * Eliminar varios items de un pack en UN solo request (batch update).
   * Usa /cart/update.js con `updates: { key1: 0, key2: 0, ... }` — más rápido
   * y atómico que llamar /cart/change.js N veces.
   */
  async function removePackByKeys(keys) {
    const updates = {};
    keys.forEach(k => { if (k) updates[k] = 0; });
    if (Object.keys(updates).length === 0) return;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: SECTION_ID,
          sections_url: window.location.pathname,
        }),
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('update failed: ' + res.status);
      const data = await res.json();
      const sectionHtml = data.sections?.[SECTION_ID];
      updateHeaderCount(data);
      if (sectionHtml) {
        applyDrawerHtml(sectionHtml);
      } else {
        await refreshDrawer();
      }
      document.dispatchEvent(new CustomEvent('lj:cart:update', { detail: { cart: data } }));
    } catch (err) {
      console.warn('[lj-cart] pack remove failed', err);
    }
  }

  // ─────────────────────────────────────────────────────────
  // Recommendations — built with DOM API (no innerHTML strings)
  // ─────────────────────────────────────────────────────────
  async function hydrateRecommendations() {
    const recos = $('[data-lj-cart-recos]');
    if (!recos) return;
    const productId = recos.dataset.productId;
    const limit = parseInt(recos.dataset.limit || '3', 10);
    if (!productId) return;

    try {
      // Regla (Pedro 2026-07-10): carrito CON Mil Ideas -> sugerir SOLO Mil
      // Ideas; carrito SIN Mil Ideas -> solo relacionados (nada de MI/hogar).
      const soloMilideas = recos.dataset.hasMilideas === 'true';
      let mixed = [];
      if (soloMilideas) {
        const [miRes, cartRes] = await Promise.all([
          fetch('/collections/mil-ideas-todos/products.json?limit=24'),
          fetch('/cart.js', { cache: 'no-store' }).catch(() => null),
        ]);
        if (!miRes.ok) return;
        const inCart = new Set();
        if (cartRes && cartRes.ok) {
          (await cartRes.json()).items.forEach(it => inCart.add(it.product_id));
        }
        // normalizar al formato de recommendations (price en centavos)
        const pool = ((await miRes.json()).products || [])
          .filter(h => h.variants && h.variants[0] && h.variants[0].available !== false && !inCart.has(h.id))
          .map(h => ({
            url: '/products/' + h.handle,
            title: h.title,
            featured_image: h.images && h.images[0] ? h.images[0].src : null,
            price: Math.round(parseFloat(h.variants[0].price) * 100),
            id: h.id,
          }));
        // rotar el punto de partida para variar las sugerencias
        const start = pool.length ? Math.floor(Math.random() * pool.length) : 0;
        for (let i = 0; i < Math.min(limit, pool.length); i++) mixed.push(pool[(start + i) % pool.length]);
      } else {
        const res = await fetch(`/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${limit + 4}&intent=related`);
        if (!res.ok) return;
        mixed = ((await res.json()).products || []).filter(p => (p.vendor || '') !== 'Mil Ideas');
      }

      const grid = recos.querySelector('.lj-cart-recos__grid');
      if (!grid) return;

      // Clear existing
      grid.replaceChildren();

      mixed.slice(0, limit).forEach(p => {
        const a = document.createElement('a');
        a.href = p.url;
        a.className = 'lj-cart-reco';

        if (p.featured_image) {
          const img = document.createElement('img');
          img.src = p.featured_image;
          img.alt = p.title || '';
          img.loading = 'lazy';
          a.appendChild(img);
        }

        const body = document.createElement('div');
        const titleP = document.createElement('p');
        titleP.className = 'lj-cart-reco__title';
        titleP.textContent = p.title || '';
        const priceP = document.createElement('p');
        priceP.className = 'lj-cart-reco__price';
        priceP.textContent = moneyFmt(p.price);
        body.appendChild(titleP);
        body.appendChild(priceP);
        a.appendChild(body);

        grid.appendChild(a);
      });

      recos.hidden = mixed.length === 0;
    } catch (err) {
      console.warn('[lj-cart] recos failed', err);
    }
  }

  // ─────────────────────────────────────────────────────────
  // Find source image for fly-to-cart from a form
  // ─────────────────────────────────────────────────────────
  function findSourceImage(form) {
    // 1. Imagen dentro del card que contiene el form (collection/bundles)
    const card = form.closest('article, .lj-bundle, .lj-product-card, .lj-rec-card, [data-product]');
    if (card) {
      const img = card.querySelector('img');
      if (img && img.getBoundingClientRect().width > 0) return img;
    }
    // 2. Página de producto: imagen featured del PDP (incluye nuestro layout)
    const featured = $(
      '.lj-pdp__main img, .product__media img, .lj-product__media img, .product-single__photo img'
    );
    if (featured && featured.getBoundingClientRect().width > 0) return featured;
    // 3. Fallback: primera imagen visible en el main
    const main = form.closest('main') || document;
    const candidates = main.querySelectorAll('img');
    for (const img of candidates) {
      const r = img.getBoundingClientRect();
      // Solo imagenes visibles (no off-screen)
      if (r.width > 50 && r.height > 50 && r.top < window.innerHeight && r.bottom > 0) {
        return img;
      }
    }
    return null;
  }

  // ─────────────────────────────────────────────────────────
  // Quick-add via button (sin form) — usado por collection-grid,
  // wishlist, recommendations, etc. Misma animación + drawer que el
  // form submit handler.
  //
  // Construye un FormData artificial a partir de los data-* attrs
  // del botón:
  //   data-variant-id="123"  (required)
  //   data-quantity="1"      (default)
  //   data-properties-pack='{"Pack":"JoeManía"}' (opcional, JSON)
  // ─────────────────────────────────────────────────────────
  async function quickAddViaButton(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');
    const originalText = btn.dataset.originalText || btn.textContent;
    if (!btn.dataset.originalText) btn.dataset.originalText = originalText;

    // Encontrar imagen del card padre para fly-to-cart
    const sourceImg = btn.closest('article, .lj-pcard, .lj-bundle')?.querySelector('img')
      || findSourceImage(btn.closest('form, section, main') || document);

    flyToCart(sourceImg);

    try {
      const body = {
        items: [{ id: parseInt(variantId, 10), quantity: parseInt(btn.dataset.quantity || '1', 10) }],
        sections: [SECTION_ID],
      };
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'No se pudo agregar al carrito');
      }
      const data = await res.json();

      const sectionHtml = data.sections?.[SECTION_ID];
      let cart;
      if (sectionHtml) {
        applyDrawerHtml(sectionHtml);
        const cr = await fetch('/cart.js', { cache: 'no-store' });
        cart = await cr.json();
      } else {
        const cr = await fetch('/cart.js', { cache: 'no-store' });
        cart = await cr.json();
        await refreshDrawer();
      }
      updateHeaderCount(cart);
      const productTitle = btn.dataset.productTitle || data.items?.[0]?.product_title || data.product_title || '';
      const productImage = data.image || data.items?.[0]?.image;
      showToast({
        title: '✓ ' + productTitle + ' agregado',
        imgSrc: productImage,
      });

      // Visual success state — restore después de 1.8s
      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      // Para botón texto, cambiar texto. Para icon button (sin texto), CSS toggle clases.
      if (btn.classList.contains('lj-pcard__btn--add')) {
        btn.textContent = '✓ Agregado al carrito';
      }
      setTimeout(() => {
        btn.classList.remove('is-success');
        if (btn.classList.contains('lj-pcard__btn--add')) {
          btn.textContent = btn.dataset.originalText;
        }
      }, 1800);

      document.dispatchEvent(new CustomEvent('lj:cart:add', { detail: { item: data, cart } }));
    } catch (err) {
      console.error('[lj-cart] quickAdd failed', err);
      btn.classList.remove('is-loading');
      // Reset
      if (btn.classList.contains('lj-pcard__btn--add')) {
        btn.textContent = btn.dataset.originalText;
      }
      alert('No se pudo agregar al carrito. Por favor intenta de nuevo.');
    }
  }

  // ─────────────────────────────────────────────────────────
  // Event delegation
  // ─────────────────────────────────────────────────────────
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;
    // El PDP (lj-product-main) maneja su propio submit (candado anti
    // doble-tap + flyToCart). Si este listener también lo procesara, un
    // solo clic agregaría 2 unidades. Este delegado es solo para quickadds.
    if (form.closest('.lj-pdp')) return;
    e.preventDefault();
    const sourceImg = findSourceImage(form);
    ajaxAddToCart(form, sourceImg);
  });

  // Listener para botones quickadd (Tienda, Categorías, Favoritos)
  document.addEventListener('click', (e) => {
    const quickaddBtn = e.target.closest('[data-lj-quickadd]');
    if (quickaddBtn) {
      e.preventDefault();
      e.stopPropagation();  // evita trigger del <a> parent
      quickAddViaButton(quickaddBtn);
      return;
    }
  }, true);  // capture phase — corre ANTES de los handlers locales

  document.addEventListener('click', (e) => {
    const openTrigger = e.target.closest('[data-lj-cart-open], .lj-cart-pill');
    if (openTrigger && !openTrigger.hasAttribute('data-lj-cart-bypass')) {
      e.preventDefault();
      openDrawer();
      return;
    }

    const closeBtn = e.target.closest('[data-lj-cart-close]');
    if (closeBtn) {
      /* Si el elemento es un <a> con href real (no "#"), dejamos que la
         navegacion ocurra naturalmente — solo cerramos el drawer. Esto fix:
          - Banner "Compra un pack y ahorra 27%" → /pages/landing-little-joe
          - Click en imagen del item del cart → /products/<handle>
          - Cualquier <a> que cierra el drawer y navega a otra pagina.
         Si NO tiene href o el href es "#"/hash, prevenimos default (boton
         puro de cerrar como el X o "Seguir comprando"). */
      const href = closeBtn.tagName === 'A' ? closeBtn.getAttribute('href') : null;
      const navigatesAway = href && href !== '#' && !href.startsWith('#');
      if (!navigatesAway) {
        e.preventDefault();
      }
      closeDrawer();
      return;
    }

    // Helper: obtener el identificador estable para el line item.
    // Preferimos `data-line-key` (item.key, único e inmutable) sobre `data-line`
    // (forloop.index, INESTABLE cuando hay packs visualmente agrupados).
    function getLineRef(btn) {
      // Buscar el <li> ancestor que tenga data-line-key
      const li = btn.closest('[data-line-key]');
      if (li && li.dataset.lineKey) return li.dataset.lineKey;
      // Fallback al data-line del propio botón (compatibilidad legacy)
      const line = parseInt(btn.dataset.line, 10);
      return isNaN(line) ? null : line;
    }

    const decBtn = e.target.closest('[data-lj-qty-decrease]');
    if (decBtn) {
      const ref = getLineRef(decBtn);
      const input = decBtn.parentElement?.querySelector('[data-lj-qty-input]');
      if (ref != null && input) {
        const newQty = Math.max(0, parseInt(input.value, 10) - 1);
        input.value = newQty;
        changeQty(ref, newQty);
      }
      return;
    }

    const incBtn = e.target.closest('[data-lj-qty-increase]');
    if (incBtn) {
      const ref = getLineRef(incBtn);
      const input = incBtn.parentElement?.querySelector('[data-lj-qty-input]');
      if (ref != null && input) {
        const newQty = parseInt(input.value, 10) + 1;
        input.value = newQty;
        changeQty(ref, newQty);
      }
      return;
    }

    const removeBtn = e.target.closest('[data-lj-qty-remove]');
    if (removeBtn) {
      const ref = getLineRef(removeBtn);
      if (ref != null) changeQty(ref, 0);
      return;
    }

    // Pack remove: elimina TODOS los items del pack en un batch
    const packRemoveBtn = e.target.closest('[data-lj-pack-remove]');
    if (packRemoveBtn) {
      const packEl = packRemoveBtn.closest('[data-pack-keys]');
      if (packEl) {
        const keysCsv = packEl.dataset.packKeys || '';
        const keys = keysCsv.split(',').filter(k => k.length > 0);
        if (keys.length > 0) removePackByKeys(keys);
      }
      return;
    }

    // Pack edit: abre el pack-builder con el pack ARMADO (no desde cero)
    // Lee el cart, filtra los items con _pack_id matching, los pasa como
    // preSelection a openPackBuilder. El modal se abre con las cards ya
    // marcadas como seleccionadas, mostrando la composición actual del
    // pack — el cliente puede agregar/quitar items sin perder lo armado.
    const packEditBtn = e.target.closest('[data-lj-pack-edit]');
    if (packEditBtn) {
      const handle = packEditBtn.dataset.ljPackEdit;
      if (!handle) return;
      const packId = packEditBtn.closest('[data-pack-id]')?.dataset.packId;

      // Helper: lee items del cart actual y construye preSelection
      const buildPreSelection = async () => {
        try {
          const cr = await fetch('/cart.js', { cache: 'no-store' });
          const cart = await cr.json();
          const matching = cart.items.filter(i => {
            const pid = i.properties?._pack_id;
            // Match por _pack_id (preciso si está disponible) o handle (fallback)
            return packId ? pid === packId : i.properties?._pack_handle === handle;
          });
          // Cada item del cart puede representar quantity > 1; expandimos
          // a una entrada por unidad para que el pack builder reconozca
          // el slot correcto (ej. 4 unidades del mismo aroma = 4 entries).
          //
          // PRICING (BUG FIX 2026-05-06): NO pasamos price ni compare_at
          // — el modal los lee de state.products (fetch autoritativo del
          // producto). Si pasamos final_price del cart, en edit mode el
          // discount se aplica DOS veces (cart ya lo descontó + modal
          // lo descuenta de nuevo) → total S/76.40 vs S/88 esperado.
          // Solo pasamos variantId, title (fallback) e image (fallback).
          const preSelection = [];
          matching.forEach(i => {
            const slotGroup = 0; // default; multi-slot packs (Joe Club) podrían necesitar mapping
            for (let q = 0; q < i.quantity; q++) {
              preSelection.push({
                handle: i.handle,
                variantId: i.variant_id,
                title: i.product_title,
                image: i.image,
                slotGroup: slotGroup,
              });
            }
          });
          return preSelection;
        } catch (err) {
          console.warn('[lj-cart] preSelection failed', err);
          return [];
        }
      };

      // Priority 1: modal disponible en la página actual
      // Pasamos packId como editingInstanceId — al guardar, el pack-builder
      // primero borrará los items con ese _pack_id antes de agregar los
      // nuevos. Esto reemplaza el pack en lugar de duplicarlo.
      if (typeof window.openPackBuilder === 'function' &&
          window.LJ_PACK_CONFIG &&
          window.LJ_PACK_CONFIG[handle]) {
        buildPreSelection().then(preSelection => {
          closeDrawer();
          setTimeout(() => {
            window.openPackBuilder(handle, preSelection, packId);
          }, 250);
        });
        return;
      }

      // Priority 2: redirigir a landing-little-joe con hash + flag edit.
      // El pack-builder.js lee `#open-pack=<handle>&edit-instance=<id>` en
      // checkAutoOpen y al cargar fetchea el cart para construir el
      // preSelection con el editingInstanceId correcto.
      var editParam = packId ? '&edit-instance=' + encodeURIComponent(packId) : '';
      window.location.href = '/pages/landing-little-joe#open-pack=' + encodeURIComponent(handle) + editParam;
      return;
    }

    const toastView = e.target.closest('[data-lj-toast-view]');
    if (toastView) {
      e.preventDefault();
      hideToast();
      openDrawer();
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const drawer = $('[data-lj-cart-drawer]');
      if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    }
  });

  document.addEventListener('DOMContentLoaded', hydrateRecommendations);

  // ─────────────────────────────────────────────────────────
  // PUBLIC API — exponer helpers en window para que otros scripts
  // (lj-pack-builder.js, etc.) puedan usar el MISMO drawer carrito
  // sin redirigir a /cart full page. Esto unifica la UX: todos los
  // add-to-cart (productos sueltos, packs, wishlist) llaman al mismo
  // drawer y mantienen consistencia visual.
  // ─────────────────────────────────────────────────────────
  window.ljCartApplyHtml        = applyDrawerHtml;
  window.ljCartUpdateHeaderCount = updateHeaderCount;
  window.ljCartOpenDrawer       = openDrawer;
  window.ljCartCloseDrawer      = closeDrawer;
  window.ljCartRefresh          = refreshDrawer;

  // Listener para `lj:cart:request-open` event — permite que cualquier
  // script externo (pack-builder, etc) dispare apertura del drawer
  // dispatchando este event en document. Usamos `request-open` para
  // diferenciar del `lj:cart:open` que es la NOTIFICACIÓN de "ya abrí"
  // (evita loop infinito si listeners llamaran openDrawer otra vez).
  document.addEventListener('lj:cart:request-open', () => {
    const drawer = $('[data-lj-cart-drawer]');
    if (drawer && drawer.getAttribute('aria-hidden') !== 'false') {
      openDrawer();
    }
  });
})();
